import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { track, revenue, Revenue } from "@amplitude/analytics-browser";
import { AnyObject } from "yup/lib/types";
import { UserDetailsRes } from "./store/features/api/generated";
import { getPermission } from "./utils/SessionManagement";
import { getRuntimeConfig } from "./getRuntimeConfig";

// Initiate Amplitude

export const initAmplitude = () => {
  const amplitudeKey = getRuntimeConfig().AMPLITUDE as string;

  if (amplitudeKey) {
    if (getRuntimeConfig().CNB_ENV === "dev") {
      // If local, directly initiate amplitude.
      // Remember to remove ad blockers to see the events in the amplitude dashboard.
      amplitude.init(amplitudeKey);
    } else {
      // If not local, proxy the amplitude requests through the server.
      amplitude.init(amplitudeKey, undefined, {
        defaultTracking: true,
        autocapture: {
          attribution: true,
          formInteractions: true,
          pageViews: true,
          sessions: true,
          elementInteractions: true,
          fileDownloads: true,
        },
      });

      // Create and Install Session Replay Plugin
      const sessionReplayTracking = sessionReplayPlugin({
        sampleRate: 0.25,
      });
      amplitude.add(sessionReplayTracking);
    }
  }
};

/**
 * Function to track user interactions (actions) within the application.
 *
 * @function trackEvent
 * @param {string} action - Describes the user's action.
 * @param {AnyObject} [details] - Details about the action, optional.
 * @param {string} [component] - Name of the component where action occurred, optional.
 * @returns {void}
 */
export const trackEvent = (action: string, details?: AnyObject, source?: string) => {
  // Prefix action with the source name if provided.
  const eventLabel = source ? `[${source}] ${action}` : action;

  // If source name is provided, add as a category property
  if (source && details) details["category"] = source;

  // Track the labelled event.
  track(eventLabel, details);
};

// Initiate Amplitude User
export const InitiateAmplitudeUser = (userDetails: UserDetailsRes) => {
  // Extracting name, customerId, and email from userDetails.
  const { customerId, email } = userDetails;

  // Retrieving permission level of the user from sessionStorage.
  const permission = getPermission();

  // Creating a new identify instance with amplitude.
  const identifyObj = new amplitude.Identify();

  // Setting "Email ID" attribute in amplitude Identify object.
  identifyObj.set("Email ID", `${email}`);

  // Setting user id if the user is verified for the current session in amplitude.
  if (permission === "user") amplitude.setUserId(`${customerId} ${email}`);
  if (permission === "guest") amplitude.setUserId(undefined);

  // Tracking the identified user.
  amplitude.identify(identifyObj);

  // If permission level is defined, set the group as "userGroup" EG: admin | guest | user.
  if (permission) amplitude.setGroup("userGroup", JSON.stringify(permission));
};

export const trackRevenue = (
  product: string,
  price: number,
  quantity: number,
  properties: AnyObject,
) => {
  const event = new Revenue()
    .setProductId(product)
    .setPrice(price)
    .setQuantity(quantity)
    .setEventProperties(properties);
  revenue(event);
};
